import { useCallback, useMemo } from 'react';
import useUnassignAdmin from 'hooks/admins/useUnassignAdmin';
import { toast } from 'react-toastify';
import useAssignAdmin from 'hooks/admins/useAssignAdmin';
import AdminsTable from 'components/admins/AdminsTable';
import { confirmAlert } from 'react-confirm-alert';
import { useIntl } from 'react-intl';
import 'components/common/AlertOverlay.scss';
import useGetAllAdmins from 'hooks/admins/useGetAdmins';
import LoadingOverlay from 'components/common/LoadingOverlay';
import type { FormikHelpers } from 'formik';
import type { IAdminFormValues, IAdminRow } from 'components/admins/AdminsTable';
import { messageOf } from 'utils/error';
import requireValue from 'utils/requireValue';

const useUnassignAdminHandler = () => {
  const unassignAdmin = useUnassignAdmin();
  const { formatMessage: t } = useIntl();

  const handleUnassign = useCallback(
    async ({ email, name }: IAdminRow) => {
      confirmAlert({
        title: t({ id: 'admins.unassign_admin' }),
        message: t({ id: 'admins.unassign_admin_message' }, { name }),
        buttons: [
          {
            label: t({ id: 'buttons.cancel' }),
          },
          {
            label: t({ id: 'buttons.unassign' }),
            onClick: async () => {
              try {
                // The schema allows an admin without an email; the backend rejected a null email before (an
                // error toast either way).
                if (email === null) throw new TypeError('The admin has no email address');
                await unassignAdmin({ email });
                toast.success(t({ id: 'admins.unassigned' }));
              } catch (error) {
                toast.error(messageOf(error));
              }
            },
          },
        ],
      });
    },
    [t, unassignAdmin],
  );
  return handleUnassign;
};

const useAssignAdminHandler = () => {
  const assignAdmin = useAssignAdmin();
  const { formatMessage: t } = useIntl();

  const handleAssign = useCallback(
    async (
      email: IAdminFormValues,
      { resetForm, setSubmitting }: FormikHelpers<IAdminFormValues>,
    ) => {
      try {
        await assignAdmin(email);
        toast.success(t({ id: 'admins.assigned' }));
        resetForm();
      } catch (error) {
        toast.error(messageOf(error));
      } finally {
        setSubmitting(false);
      }
    },
    [assignAdmin, t],
  );
  return handleAssign;
};

const AdminsPage = () => {
  const [data, loading] = useGetAllAdmins();
  const unassignAdmin = useUnassignAdminHandler();
  const assignAdmin = useAssignAdminHandler();
  const { formatMessage: t } = useIntl();

  const fields = useMemo(
    () => [
      { key: 'name', label: t({ id: 'admins.name' }) },
      { key: 'email', label: t({ id: 'admins.email' }) },
      { key: 'unassign', label: t({ id: 'admins.unassign' }) },
    ],
    [t],
  );

  const admins = useMemo(
    () =>
      // Reading the list, an admin, its profile or first email throws when it is missing, as it did.
      requireValue(data, 'map').map((admin): IAdminRow => {
        const { emails, profile } = requireValue(admin, 'emails');
        const { firstName, lastName } = requireValue(profile, 'firstName');
        return {
          name: `${firstName} ${lastName}`,
          email: requireValue(requireValue(emails, '0')[0], 'address').address,
        };
      }),
    [data],
  );

  if (loading) {
    return <LoadingOverlay />;
  }

  return <AdminsTable {...{ assignAdmin, unassignAdmin, fields, admins }} />;
};

export default AdminsPage;
